import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  VStack,
  Flex,
  Text,
  IconButton,
  HStack,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Portal,
  Tooltip,
} from "@chakra-ui/react";
import FloatingChatbot from "./FloatingChatbot";
import strategyWorkflowManager from "../services/StrategyWorkflowManager";
import ConversationsList from "./ConversationsList";
import chatStateManager from "../services/ChatStateManager";
import workflowRegistry from "../services/WorkflowRegistry";
import { STRATEGY_COMPLETE_MESSAGE, buildReturningUserMessage } from '../utils/postStrategyMessages';
import competitorAnalysisWorkflowService from '../services/CompetitorAnalysisWorkflowService';
import { fetchWithAuth } from '../utils/fetchWithAuth';

// CRITICAL: Durable launch registry - prevents main workflow from re-triggering
// when manual feature enablement changes state
const autonomousLaunchRegistry = {
  consumed: new Map(),
  hasConsumed(key) {
    return this.consumed.has(key);
  },
  markConsumed(key) {
    this.consumed.set(key, true);
  },
  clear(key) {
    this.consumed.delete(key);
  }
};

// CRITICAL: Durable registry for FEATURE ENABLEMENT workflows
// Completely separate from main workflow registry to prevent cross-contamination
// This prevents feature workflows from triggering multiple times after first step completes
const featureEnablementRegistry = {
  consumed: new Map(),
  hasConsumed(featureId) {
    return this.consumed.has(featureId);
  },
  markConsumed(featureId, executionId) {
    console.log(`🔒 FEATURE REGISTRY: Marking ${featureId} as consumed (execution: ${executionId})`);
    this.consumed.set(featureId, { executionId, timestamp: Date.now() });
  },
  clear(featureId) {
    console.log(`🧹 FEATURE REGISTRY: Clearing ${featureId}`);
    this.consumed.delete(featureId);
  },
  clearAll() {
    console.log("🧹 FEATURE REGISTRY: Clearing all entries");
    this.consumed.clear();
  }
};

// Module-level helper — no hooks, so safe outside the component
async function performWelcomeInjection(planId, chatbotRef) {
  let hasCompetitorReport = false;
  let isShared = false;
  try {
    const [competitorReport, shareResponse] = await Promise.all([
      competitorAnalysisWorkflowService.loadLatestReport(planId).catch(() => null),
      fetchWithAuth(`/api/plans/${planId}/share`).catch(() => null),
    ]);
    hasCompetitorReport = !!competitorReport;
    if (shareResponse?.ok) {
      const shareData = await shareResponse.json();
      isShared = !!shareData?.isShared;
    }
  } catch (e) {
    // Silent fail — use defaults
  }
  if (chatbotRef.current?.addMessage) {
    chatbotRef.current.addMessage({
      text: buildReturningUserMessage({ hasCompetitorReport, isShared }),
      sender: 'bot',
      isHtml: false,
      timestamp: new Date().toISOString(),
    });
  }
}

const EmbeddedChatPanel = React.forwardRef(
  (
    {
      currentDesignChallenge,
      selectedPrograms,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      finalProgramSequence,
      totalDuration,
      handleComponentAction,
      isStrategyCreationMode,
      setIsStrategyCreationMode,
      programsOfWork,
      methods,
      tools,
      deliverables,
      onCollapse,
      isNarrativeCollapsed,
      currentPlanId,
      currentWorkflowId,
      uiConfig,
      executionPlan,
      shouldAutoLaunchWorkflow,
      onNavigateToView,
      currentView,
      onOpenShareModal,
      onOpenMcpModal,
      onOpenLearnings,
      onLearningsCountChanged,
      onExecutionStateChange,
    },
    forwardedChatbotRef,
  ) => {
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);

    // CRITICAL: Use sessionStorage to track if workflow has been initialized
    // Persists across component re-mounts and React Strict Mode cycles
    const getWorkflowInitializedKey = () => {
      if (!location.state?._cacheKey) return null;
      return `workflow_initialized_${location.state._cacheKey}`;
    };
    
    const hasWorkflowBeenInitialized = () => {
      const key = getWorkflowInitializedKey();
      if (!key) return false;
      return sessionStorage.getItem(key) === 'true';
    };
    
    const markWorkflowAsInitialized = () => {
      const key = getWorkflowInitializedKey();
      if (key) {
        sessionStorage.setItem(key, 'true');
        console.log("✅ Marked workflow as initialized in sessionStorage:", key);
      }
    };

    // Sync internal state with parent state
    React.useEffect(() => {
      console.log(
        "🔥 EmbeddedChatPanel: isNarrativeCollapsed changed to",
        isNarrativeCollapsed,
      );
      setIsCollapsed(isNarrativeCollapsed);
    }, [isNarrativeCollapsed]);
    // CRITICAL FIX: Keep separate refs to avoid recursion
    // localChatbotRef -> always points to FloatingChatbot child
    // forwardedChatbotRef -> exposed to parent via useImperativeHandle
    // DO NOT alias these - that causes addMessage recursion
    const localChatbotRef = useRef(null);
    const outerPanelRef = useRef(null); // diagnostic ref
    const welcomeTimerRef = useRef(null);
    const welcomeInjectedRef = useRef(false);

    // Strategy workflow state with execution tracking
    const [isExecutingWorkflow, setIsExecutingWorkflow] = useState(false);
    const [workflowInitialized, setWorkflowInitialized] = useState(false);
    const [workflowExecutionId, setWorkflowExecutionId] = useState(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [workflowStatus, setWorkflowStatus] = useState(null);
    
    // CRITICAL: Separate flag for feature enablement workflow - blocks main workflow when active
    const [isFeatureWorkflowExecuting, setIsFeatureWorkflowExecuting] = useState(false);

    // Use useRef to track execution state - survives React re-renders and strict mode
    const executionStateRef = useRef({
      hasExecuted: false,
      executionId: null,
      fingerprint: null,
      lastExecution: 0,
    });

    // CRITICAL: useRef for feature workflow execution state - survives React re-renders
    // This mirrors the pattern used for main workflow to prevent duplicate triggers
    const featureExecutionStateRef = useRef({
      hasExecuted: false,
      executionId: null,
      featureId: null,
      lastExecution: 0,
    });

    // CRITICAL FIX: Store callbacks in a ref that survives StrictMode
    // This ensures the workflow always has FRESH callbacks from the stable mount
    const workflowCallbacksRef = useRef(null);
    
    // CRITICAL FIX: Track if we've already launched (separate from execution state)
    // This allows callback re-registration while preventing duplicate launches
    const hasLaunchedRef = useRef(false);

    // CRITICAL FIX: Derive autonomous mode from WorkflowRegistry instead of relying on prop
    // This avoids React Strict Mode race conditions with useState initialization
    const isAutonomousMode = React.useMemo(() => {
      console.log("🤖 EmbeddedChatPanel: Calculating autonomous mode from registry", {
        currentWorkflowId,
        locationWorkflowType: location.state?.workflowType,
      });

      // Check currentWorkflowId first (most reliable)
      if (currentWorkflowId) {
        const workflow = workflowRegistry.getWorkflow(currentWorkflowId);
        const autonomous = workflow?.uiComponents?.chatPanel?.autonomousMode === true;
        console.log("🤖 Found workflow config:", { currentWorkflowId, autonomous });
        return autonomous;
      }
      
      // Fallback: check location.state.workflowType
      if (location.state?.workflowType) {
        const workflow = workflowRegistry.getWorkflow(location.state.workflowType);
        const autonomous = workflow?.uiComponents?.chatPanel?.autonomousMode === true;
        console.log("🤖 Found workflow config from location:", { workflowType: location.state.workflowType, autonomous });
        return autonomous;
      }

      console.log("🤖 No workflow config found, defaulting to false");
      return false;
    }, [currentWorkflowId, location.state?.workflowType]);

    // Create unique execution fingerprint for tracking
    const executionFingerprint = React.useMemo(() => {
      if (!currentDesignChallenge) return null;

      if (isAutonomousMode) {
        return `autonomous_${currentDesignChallenge}`;
      }

      return `${currentDesignChallenge}_${programsOfWork?.length || 0}_${methods?.length || 0}_${tools?.length || 0}`;
    }, [
      currentDesignChallenge,
      isAutonomousMode,
      programsOfWork,
      methods,
      tools,
    ]);

    // CRITICAL FIX: Create durable launch key from location state or planId
    const durableLaunchKey = location.state?._cacheKey || currentPlanId || 'unknown';

    // Main workflow execution effect with TWO-PHASE pattern for StrictMode safety
    // PHASE 1: Always refresh callbacks in ref (runs every render)
    // PHASE 2: Only launch workflow once (gated by hasLaunchedRef)
    useEffect(() => {
      const now = Date.now();

      // ============== PHASE 1: CALLBACK REFRESH (runs EVERY render) ==============
      // Build fresh callbacks and store in ref BEFORE any guards
      // This ensures StrictMode second render can refresh stale callbacks
      const freshCallbacks = {
        onChatMessage: (message) => {
          console.log("📝 [FRESH CALLBACK] Workflow message:", message.text?.substring(0, 50) + "...");
          if (localChatbotRef.current && typeof localChatbotRef.current.addMessage === "function") {
            localChatbotRef.current.addMessage(message);
          }
        },
        onStepUpdate: (stepIndex, step) => {
          console.log(`🔄 [FRESH CALLBACK] Step ${stepIndex} updated:`, step.title, step.status);
        },
        onConversationUpdate: (planId) => {
          console.log("🔗 [FRESH CALLBACK] Updating conversation with plan ID:", planId);
        },
        onComplete: (workflowResults) => {
          console.log("🎉🎉🎉 [FRESH CALLBACK] COMPLETION PATH: onComplete called");
          console.log("🎉 Workflow completed with results:", workflowResults);
          
          // Reset execution state after successful completion
          setIsExecutingWorkflow(false);
          setWorkflowExecutionId(null);
          executionStateRef.current.hasExecuted = true;
          executionStateRef.current.fingerprint = `${isStrategyCreationMode}_${currentDesignChallenge?.slice(0, 50)}`;
          executionStateRef.current.executionId = null;
          
          // Forward results to component action handler
          if (handleComponentAction) {
            console.log("🎉 Calling handleComponentAction with strategy_creation_complete");
            
            // Create the onSaveComplete callback
            const onSaveCompleteCallback = (savedPlanId) => {
              console.log("✅ [EmbeddedChatPanel] onSaveComplete callback invoked with planId:", savedPlanId);
              chatStateManager.updateConversationPlanId(savedPlanId);
              // Inject the living strategy framing + guided next steps
              if (localChatbotRef.current?.addMessage) {
                localChatbotRef.current.addMessage({
                  text: STRATEGY_COMPLETE_MESSAGE,
                  sender: "bot",
                  isHtml: false,
                });
              }
            };
            
            console.log("🔍 DEBUG: onSaveComplete callback created", {
              isFunction: typeof onSaveCompleteCallback === 'function'
            });
            
            handleComponentAction("strategy_creation_complete", null, {
              ...workflowResults,
              onSaveComplete: onSaveCompleteCallback,
              onSaveError: (error) => {
                console.error("❌ Strategy save failed:", error);
              },
            });
          } else {
            console.error("❌❌❌ COMPLETION PATH BROKEN: handleComponentAction is null/undefined!");
          }
        },
        onError: (error) => {
          console.error("❌ [FRESH CALLBACK] Workflow failed with error:", error);
          setIsExecutingWorkflow(false);
          setWorkflowInitialized(false);
          setWorkflowExecutionId(null);
          executionStateRef.current.hasExecuted = true;
          executionStateRef.current.fingerprint = `${isStrategyCreationMode}_${currentDesignChallenge?.slice(0, 50)}_ERROR`;
          executionStateRef.current.executionId = null;
          if (localChatbotRef.current) {
            localChatbotRef.current.addMessage({
              text: "❌ Failed to complete the strategy creation process. Please try again.",
              sender: "bot",
              isHtml: false,
            });
          }
        },
      };
      
      // CRITICAL: Always update the ref with fresh callbacks
      workflowCallbacksRef.current = freshCallbacks;
      console.log("🔄 [PHASE 1] Refreshed workflowCallbacksRef with fresh callbacks");

      // ============== PHASE 2: WORKFLOW LAUNCH (runs ONCE) ==============
      // CRITICAL: Block main workflow when feature enablement is running
      if (isFeatureWorkflowExecuting) {
        console.log("🔐 FEATURE GUARD: Feature workflow in progress - blocking main workflow");
        return;
      }

      // CRITICAL FIX: Simplified launch check - Brief flows MUST work
      const shouldStart = !!(location.state?.initiateStrategyCreation || shouldAutoLaunchWorkflow);

      console.log("🚨 [PHASE 2] WORKFLOW LAUNCH CHECK:", {
        shouldStart,
        hasLaunched: hasLaunchedRef.current,
        durableLaunchConsumed: autonomousLaunchRegistry.hasConsumed(durableLaunchKey),
        hasDesignChallenge: !!currentDesignChallenge,
      });

      // Check shouldStart FIRST - prevents saved plans from triggering
      if (!shouldStart) {
        console.log("🚫 SAVED PLAN GUARD: shouldStart is FALSE");
        if (currentPlanId && autonomousLaunchRegistry.hasConsumed(currentPlanId)) {
          autonomousLaunchRegistry.clear(currentPlanId);
        }
        return;
      }

      // CRITICAL: Use hasLaunchedRef to prevent duplicate launches
      // This allows callback refresh (Phase 1) while blocking re-launch
      if (hasLaunchedRef.current) {
        console.log("🔐 LAUNCH GUARD: Already launched - callbacks refreshed, skipping re-launch");
        return;
      }

      // Check durable launch registry (survives completion handler resets)
      if (autonomousLaunchRegistry.hasConsumed(durableLaunchKey)) {
        console.log("🔐 DURABLE GUARD: Autonomous launch already consumed for", durableLaunchKey);
        return;
      }

      // Prevent duplicate executions with fingerprint checking
      if (executionStateRef.current.hasExecuted && 
          executionStateRef.current.fingerprint === executionFingerprint) {
        console.log("⚠️ Duplicate execution prevented by fingerprint check");
        return;
      }

      // Check if conditions are met for workflow execution
      if (currentDesignChallenge && currentDesignChallenge.trim().length > 0 &&
          !isExecutingWorkflow && !workflowInitialized && !workflowExecutionId) {
        
        // CRITICAL: Mark as launched FIRST to prevent StrictMode duplicate
        hasLaunchedRef.current = true;
        autonomousLaunchRegistry.markConsumed(durableLaunchKey);
        
        executionStateRef.current.hasExecuted = true;
        executionStateRef.current.fingerprint = executionFingerprint;
        executionStateRef.current.lastExecution = now;

        console.log("🚀 [PHASE 2] Starting workflow execution", {
          isAutonomousMode,
          hasDesignChallenge: !!currentDesignChallenge,
          fingerprint: executionFingerprint,
          durableLaunchKey,
        });

        // Set state for UI updates
        setWorkflowInitialized(true);
        setIsExecutingWorkflow(true);

        // Create unique execution ID
        const executionId = `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setWorkflowExecutionId(executionId);
        executionStateRef.current.executionId = executionId;

        try {
          initiateStrategyWorkflow(executionId);
        } catch (error) {
          console.error("❌ Workflow execution failed:", error);
          setWorkflowInitialized(false);
          setIsExecutingWorkflow(false);
          setWorkflowExecutionId(null);
          executionStateRef.current.hasExecuted = false;
          executionStateRef.current.fingerprint = null;
          executionStateRef.current.executionId = null;
          hasLaunchedRef.current = false;
        }
      }
    }, [
      shouldAutoLaunchWorkflow,
      isAutonomousMode,
      currentDesignChallenge,
      isExecutingWorkflow,
      workflowInitialized,
      workflowExecutionId,
      executionFingerprint,
      programsOfWork,
      methods,
      tools,
      currentPlanId,
      location.pathname,
      location.state?.initiateStrategyCreation,
      location.state?._cacheKey,
      isFeatureWorkflowExecuting,
      handleComponentAction,
      isStrategyCreationMode,
      durableLaunchKey,
    ]);

    const initiateStrategyWorkflow = async (executionId) => {
      // Multiple guards against duplicate execution
      if (
        isExecutingWorkflow &&
        workflowExecutionId &&
        workflowExecutionId !== executionId
      ) {
        console.log("⚠️ Workflow already executing, blocking duplicate:", {
          currentExecutionId: workflowExecutionId,
          newExecutionId: executionId,
        });
        return;
      }

      // CRITICAL FIX: isAutonomousMode is already calculated from WorkflowRegistry via useMemo
      // No need to check again - just use it directly
      console.log("🤖 EmbeddedChatPanel: Using pre-calculated autonomous mode:", {
        currentWorkflowId,
        isAutonomousMode,
      });

      if (!currentDesignChallenge) {
        console.error("❌ Missing required data for strategy workflow");
        // Don't reset execution tracking on missing data - this is a validation error
        return;
      }

      // Validate required data based on mode
      if (!isAutonomousMode && (!programsOfWork || !methods || !tools)) {
        console.log(
          "⚠️ Missing required CSV data for template-based workflow:",
          {
            hasDesignChallenge: !!currentDesignChallenge,
            hasProgramsOfWork: !!programsOfWork,
            hasMethods: !!methods,
            hasTools: !!tools,
          },
        );
        return;
      }

      console.log(
        "🎯 Starting SINGLE strategy workflow execution:",
        executionId,
      );

      // Quick Plan: delegate to SSE via pabs:autoStartGeneration (create_plan capability)
      // FloatingChatbot handles the W-phase rendering; EmbeddedChatPanel handles completion.
      if (currentWorkflowId === 'quick_plan') {
        console.log("🚀 Quick Plan: delegating to SSE create_plan via pabs:autoStartGeneration");
        // executeGenerationWorkflow adds the user message + advisory + W phase itself for create_plan.
        window.dispatchEvent(new CustomEvent('pabs:autoStartGeneration', {
          detail: { capability: 'create_plan' }
        }));
        // SSE lifecycle is owned by FloatingChatbot — reset local execution flags
        setIsExecutingWorkflow(false);
        setWorkflowInitialized(false);
        setWorkflowExecutionId(null);
        executionStateRef.current.hasExecuted = true;
        executionStateRef.current.fingerprint = `quick_plan_ssestarted_${currentDesignChallenge?.slice(0, 50)}`;
        executionStateRef.current.executionId = null;
        return;
      }

      // Get steps from the service (single source of truth)
      // Pass currentWorkflowId to get correct steps for Decision Call vs Full Strategy
      // FIXED: currentWorkflowId is now guaranteed to be set by PlanPage's loading gate
      const steps = await strategyWorkflowManager.getWorkflowSteps(currentWorkflowId);
      
      console.log(`📊 EmbeddedChatPanel: Loaded ${steps.length} steps for workflow ${currentWorkflowId}`, {
        stepTitles: steps.map(s => s.title)
      });

      const workflowParams = {
        designChallenge: currentDesignChallenge,
        programsOfWork,
        methods,
        tools,
        deliverables,
        isAutonomousMode,
        executionPlan, // CRITICAL FIX: Pass execution plan to control which steps run
        enabledFeatures: uiConfig?.enabledFeatures || [], // Pass enabled features for smart AI recommendations
      };

      console.log("🔧 EmbeddedChatPanel: Workflow params with execution plan:", {
        currentWorkflowId,
        hasExecutionPlan: !!executionPlan,
        executionPlanSteps: executionPlan?.steps,
        executionPlanSkipSteps: executionPlan?.skipSteps,
        isAutonomousMode
      });

      try {
        // Initialize and start strategy workflow immediately
        console.log(
          "🚀 Starting strategy workflow for:",
          currentDesignChallenge,
        );

        // CRITICAL FIX: Use callbacks from ref - these are always FRESH
        // The useEffect updates workflowCallbacksRef.current on every render
        // This ensures StrictMode second render provides fresh callbacks
        const callbacks = workflowCallbacksRef.current;
        
        if (!callbacks) {
          console.error("❌ workflowCallbacksRef.current is null - callbacks not initialized");
          throw new Error("Callbacks not initialized");
        }
        
        console.log("✅ Using fresh callbacks from workflowCallbacksRef", {
          hasOnComplete: !!callbacks.onComplete,
          hasOnChatMessage: !!callbacks.onChatMessage,
          hasOnError: !!callbacks.onError,
        });

        // Start the workflow with execution tracking
        console.log(
          `🎯 Calling strategyWorkflowManager.startStrategyWorkflow for execution (${currentWorkflowId}):`,
          executionId,
          {
            currentWorkflowId,
            isAutonomousMode
          }
        );
        await strategyWorkflowManager.startStrategyWorkflow(
          currentWorkflowId,
          workflowParams,
          callbacks,
        );
      } catch (error) {
        console.error(
          "❌ Failed to start strategy workflow for execution:",
          executionId,
          error,
        );

        // Reset execution state but PREVENT auto-restart by marking as executed
        setIsExecutingWorkflow(false);
        setWorkflowInitialized(false);
        setWorkflowExecutionId(null);
        executionStateRef.current.hasExecuted = true; // KEEP TRUE to prevent auto-restart
        executionStateRef.current.fingerprint = `${isStrategyCreationMode}_${currentDesignChallenge?.slice(0, 50)}_ERROR`;
        executionStateRef.current.executionId = null;

        if (localChatbotRef.current) {
          localChatbotRef.current.addMessage({
            text: "❌ Failed to start the strategy creation process. Please try again.",
            sender: "bot",
            isHtml: false,
          });
        }
      }
    };

    const toggleCollapse = () => {
      const prevCollapsed = isCollapsed;
      console.log("🔍 EmbeddedChatPanel: toggleCollapse called", {
        prevCollapsed,
        newCollapsed: !isCollapsed,
      });
      setIsCollapsed(!isCollapsed);
      if (onCollapse) {
        onCollapse(!prevCollapsed);
      }
    };

    // Enhanced plan context for AI agents with detailed tool information
    const enrichedProgramSequence =
      finalProgramSequence?.map((program) => ({
        ...program,
        toolsByStage: program.toolsByStage
          ? Object.entries(program.toolsByStage).reduce(
              (acc, [stageName, stageData]) => {
                acc[stageName] = {
                  ...stageData,
                  tools:
                    stageData.tools?.map((tool) => ({
                      ...tool,
                      currentDuration: tool.duration || 0, // Ensure duration is always available
                    })) || [],
                };
                return acc;
              },
              {},
            )
          : {},
      })) || [];

    const planContext = {
      currentDesignChallenge,
      shortSummary,
      goalStatement,
      problemStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      selectedPrograms,
      finalProgramSequence: enrichedProgramSequence,
      totalDuration,
      currentPlanId,
      currentWorkflowId,
      uiConfig,
    };

    const handlePopoverOpen = () => {
      setIsPopoverOpen(true);
    };

    const handlePopoverClose = () => {
      setIsPopoverOpen(false);
      // Reset any scroll caused by browser focus-restoration on overflow:hidden ancestors
      requestAnimationFrame(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    };

    // Initialize workflow based on location state - REGISTRY-DRIVEN
    useEffect(() => {
      // CRITICAL: Guard against re-triggering - check sessionStorage
      if (hasWorkflowBeenInitialized()) {
        console.log("⏭️ EmbeddedChatPanel: Skipping workflow initialization - already initialized (sessionStorage check)");
        return;
      }

      // CRITICAL: Guard against re-triggering after plan is saved
      // If currentPlanId exists, the workflow has completed and saved successfully
      if (currentPlanId) {
        console.log("⏭️ EmbeddedChatPanel: Skipping workflow initialization - plan already saved with ID:", currentPlanId);
        markWorkflowAsInitialized(); // Mark as initialized to prevent future triggers
        return;
      }

      if (location.state?.initiateStrategyCreation && currentDesignChallenge) {
        console.log(
          "🎯 EmbeddedChatPanel: Detected strategy creation request from location state",
        );

        // REGISTRY-DRIVEN: Check if this is an autonomous workflow by reading from WorkflowRegistry
        let isAutonomous = false;
        if (location.state?.workflowType) {
          const workflow = workflowRegistry.getWorkflow(location.state.workflowType);
          isAutonomous = workflow?.uiComponents?.chatPanel?.autonomousMode === true;
        }

        if (isAutonomous) {
          console.log(
            "🧠 EmbeddedChatPanel: Autonomous workflow mode detected from registry",
          );
          if (setIsStrategyCreationMode) {
            setIsStrategyCreationMode("autonomous");
          }
        } else {
          if (setIsStrategyCreationMode) {
            setIsStrategyCreationMode(true);
          }
        }

        setWorkflowInitialized(false); // Allow initialization
        markWorkflowAsInitialized(); // Mark as initialized to prevent re-triggers
        console.log("✅ EmbeddedChatPanel: Workflow initialization triggered - marked as initialized");
      }
    }, [location.state, currentDesignChallenge, setIsStrategyCreationMode, currentPlanId]);

    // Manual feature workflow trigger - used when user adds features via "+" button
    // This is SEPARATE from:
    // 1. Brief page flow (uses navigation state)
    // 2. Agent-proposed feature enablement (uses FeatureEnablementConfirmation)
    // CRITICAL FIX: Wrap in useCallback to prevent recreation on every render
    // This was causing useImperativeHandle to re-run and trigger cascading re-renders
    const triggerManualFeatureWorkflow = React.useCallback(async (featureId) => {
      const now = Date.now();
      const executionId = `feature_${featureId}_${now}`;
      
      console.log("═══════════════════════════════════════════════════════════════");
      console.log("🎯 FEATURE ENABLEMENT TRACE [1/10]: triggerManualFeatureWorkflow START");
      console.log("🎯 Feature ID:", featureId);
      console.log("🎯 Execution ID:", executionId);
      console.log("🎯 Current state:", { 
        currentDesignChallenge: currentDesignChallenge?.substring(0, 50) + "...",
        currentPlanId,
        currentWorkflowId,
        hasTools: !!tools?.length,
        hasMethods: !!methods?.length,
        hasProgramsOfWork: !!programsOfWork?.length
      });
      console.log("═══════════════════════════════════════════════════════════════");
      
      // CRITICAL GUARD 1: Check durable registry FIRST (survives React re-renders and completion handlers)
      if (featureEnablementRegistry.hasConsumed(featureId)) {
        console.log(`🔐 FEATURE GUARD: Feature ${featureId} already consumed in durable registry - blocking duplicate`);
        return;
      }
      
      // CRITICAL GUARD 2: Check useRef state (survives React re-renders)
      if (featureExecutionStateRef.current.hasExecuted && 
          featureExecutionStateRef.current.featureId === featureId &&
          now - featureExecutionStateRef.current.lastExecution < 5000) {
        console.log(`🔐 FEATURE GUARD: Feature ${featureId} recently executed via ref - blocking duplicate`);
        return;
      }
      
      // CRITICAL GUARD 3: Check state flag (may be stale but still useful)
      if (isFeatureWorkflowExecuting) {
        console.log(`🔐 FEATURE GUARD: Feature workflow already executing - blocking duplicate`);
        return;
      }
      
      // CRITICAL: Mark as consumed in DURABLE registry FIRST (before any state changes)
      // This prevents completion handler from allowing re-trigger
      featureEnablementRegistry.markConsumed(featureId, executionId);
      
      // Mark as executed in useRef (survives React strict mode)
      featureExecutionStateRef.current.hasExecuted = true;
      featureExecutionStateRef.current.executionId = executionId;
      featureExecutionStateRef.current.featureId = featureId;
      featureExecutionStateRef.current.lastExecution = now;
      
      // NOW set state flags (these can trigger re-renders but guards protect us)
      setIsFeatureWorkflowExecuting(true);
      setIsExecutingWorkflow(true);
      
      // Small delay to ensure the blocking flags are applied before clearing chat
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Now safe to clear chat - the main workflow useEffect is blocked
      if (localChatbotRef.current?.startNewChat) {
        console.log("🔄 triggerManualFeatureWorkflow: Starting fresh chat conversation for isolation");
        localChatbotRef.current.startNewChat(true); // forceReset = true
      }
      
      // Reset other workflow state (but keep isFeatureWorkflowExecuting TRUE)
      setWorkflowExecutionId(null);
      setWorkflowInitialized(false);
      
      // Another small delay to let chat clear complete
      await new Promise(resolve => setTimeout(resolve, 50));

      // MANUAL-TRIGGER MODE: step_by_step_view has a Generate button - no chat workflow
      // Check BEFORE template fetch to avoid registry cleanup issues on fetch failure
      if (featureId === 'step_by_step_view') {
        console.log(`✨ Manual-trigger enablement for ${featureId} - user will click Generate button`);
        setIsFeatureWorkflowExecuting(false);
        setIsExecutingWorkflow(false);
        
        // Clear durable registry so future manual triggers work
        featureEnablementRegistry.clear(featureId);
        featureExecutionStateRef.current.hasExecuted = false;
        featureExecutionStateRef.current.executionId = null;
        featureExecutionStateRef.current.featureId = null;
        
        // Just notify parent to enable the feature - NO chat messages
        if (handleComponentAction) {
          handleComponentAction("feature_enabled", null, {
            featureId,
            triggeredManually: true,
            isManualTrigger: true,
          });
        }
        return; // Done - no workflow execution, no chat messages
      }

      try {
        // SCALABLE: Fetch templates dynamically from API (not hardcoded)
        console.log("🎯 FEATURE ENABLEMENT TRACE [2/10]: Fetching templates from API...");
        const templates = await workflowRegistry.fetchFeatureEnablementTemplates(featureId);
        console.log("🎯 FEATURE ENABLEMENT TRACE [3/10]: Templates received:", {
          featureName: templates.featureName,
          mode: templates.mode,
          hasSteps: !!templates.steps?.length,
          stepsCount: templates.steps?.length,
          steps: templates.steps?.map(s => s.id),
          intro: templates.intro?.substring(0, 50) + "..."
        });

        // VIEW-ONLY MODE: Just enable the feature without running AI workflow
        if (templates.mode === 'view') {
          console.log(`✨ View-only enablement for ${featureId} - no AI workflow needed`);
          setIsFeatureWorkflowExecuting(false); // Clear feature flag
          setIsExecutingWorkflow(false); // Reset since no workflow runs
          
          // Show quick enablement message
          if (localChatbotRef.current?.addMessage) {
            localChatbotRef.current.addMessage({
              text: templates.viewOnlyMessage || templates.completion,
              sender: "bot",
              isHtml: false,
              isFeatureEnablementComplete: true,
              featureId: featureId,
            });
          }
          
          // Notify parent to enable the feature (no workflow results needed)
          if (handleComponentAction) {
            handleComponentAction("feature_enabled", null, {
              featureId,
              triggeredManually: true,
              isViewOnly: true,
            });
          }
          return; // Done - no workflow execution needed
        }

        // WORKFLOW MODE: Run AI workflow to generate content
        if (!currentDesignChallenge) {
          console.error("❌ triggerManualFeatureWorkflow: No design challenge available");
          setIsFeatureWorkflowExecuting(false); // Clear feature flag
          setIsExecutingWorkflow(false); // Reset on error
          if (localChatbotRef.current?.addMessage) {
            localChatbotRef.current.addMessage({
              text: "Unable to generate content - no design challenge found. Please ensure your plan has a design challenge defined.",
              sender: "bot",
              isHtml: false,
            });
          }
          return;
        }

        const workflowId = 'feature_enablement';
        const executionId = `${workflowId}_${Date.now()}`;
        console.log("🎯 FEATURE ENABLEMENT TRACE [4/10]: Creating execution context", { 
          workflowId, 
          executionId, 
          featureId,
          hasDesignChallenge: !!currentDesignChallenge,
          designChallengeLength: currentDesignChallenge?.length
        });

        // Set execution ID (isExecutingWorkflow already set at function start)
        setWorkflowExecutionId(executionId);

        // NOTE: Do NOT send intro or progress UI here - StrategyWorkflowManager handles it
        // This prevents duplicate progress UIs in the chat
        console.log("🎯 triggerManualFeatureWorkflow: Letting StrategyWorkflowManager handle intro + progress UI");

        const workflowParams = {
          designChallenge: currentDesignChallenge,
          programsOfWork,
          methods,
          tools,
          deliverables,
          isAutonomousMode: true,
          triggeredManually: true,
          featureId,
          continueConversation: true, // Don't reset chat history
          enabledFeatures: uiConfig?.enabledFeatures || [], // Pass enabled features for smart AI recommendations
          executionPlan: {
            steps: ['generate_programs'],
            skipSteps: ['generate_strategy_overview', 'generate_timeline']
          }
        };

        console.log("🎯 FEATURE ENABLEMENT TRACE [5/10]: Workflow params prepared:", {
          hasDesignChallenge: !!currentDesignChallenge,
          designChallengePreview: currentDesignChallenge?.substring(0, 100),
          triggeredManually: true,
          featureId,
          continueConversation: true,
          executionPlanSteps: workflowParams.executionPlan.steps,
          programsOfWorkCount: programsOfWork?.length,
          methodsCount: methods?.length,
          toolsCount: tools?.length
        });

        const callbacks = {
          onChatMessage: (message) => {
            // Pass through ALL messages from StrategyWorkflowManager (it handles intro + progress)
            console.log("🎯 FEATURE ENABLEMENT TRACE [CALLBACK]: onChatMessage received:", {
              isProgressUI: message.isProgressUI,
              isProgressUIUpdate: message.isProgressUIUpdate,
              textPreview: message.text?.substring(0, 80),
              sender: message.sender,
              stepsCount: message.workflowSteps?.length
            });
            if (localChatbotRef.current?.addMessage) {
              localChatbotRef.current.addMessage(message);
            }
          },
          onStepUpdate: (stepIndex, step) => {
            console.log(`🔄 Manual workflow step ${stepIndex} updated:`, step.name || step.title, step.status);
            // Update progress UI with step status (only if steps exist)
            // CRITICAL FIX: Use isProgressUIUpdate (not isProgressUI) to UPDATE existing progress UI
            // Using isProgressUI would add a NEW progress UI, causing duplicates
            if (localChatbotRef.current?.addMessage && templates.steps && templates.steps.length > 0) {
              const updatedSteps = templates.steps.map((s, idx) => ({
                id: s.id,
                name: s.label,
                title: s.label, // Add both for UI compatibility
                status: idx < stepIndex ? "completed" : (idx === stepIndex ? step.status : "pending"),
                stepNumber: idx + 1,
              }));
              localChatbotRef.current.addMessage({
                isProgressUIUpdate: true, // FIX: Update existing UI, don't create new one
                workflowSteps: updatedSteps,
                currentStep: stepIndex,
                sender: "bot",
                autonomousMode: true,
                isFeatureEnablement: true,
                featureId: featureId,
              });
            }
          },
          onConversationUpdate: (planId) => {
            console.log("🔗 Manual workflow: Updating conversation with plan ID:", planId);
          },
          onComplete: (workflowResults) => {
            console.log("═══════════════════════════════════════════════════════════════");
            console.log("🎯 FEATURE ENABLEMENT TRACE [9/10]: onComplete CALLED");
            console.log("🎯 Workflow results received:", {
              hasResults: !!workflowResults,
              resultKeys: Object.keys(workflowResults || {}),
              hasFinalProgramSequence: !!workflowResults?.finalProgramSequence,
              programCount: workflowResults?.finalProgramSequence?.length,
              hasShortSummary: !!workflowResults?.shortSummary,
              hasGoalStatement: !!workflowResults?.goalStatement
            });
            console.log("═══════════════════════════════════════════════════════════════");
            
            // CRITICAL: Clear durable registry on SUCCESSFUL completion
            // This allows the feature to be triggered again if user wants to regenerate
            console.log(`🧹 FEATURE COMPLETION: Clearing durable registry for ${featureId}`);
            featureEnablementRegistry.clear(featureId);
            
            // Reset useRef state on completion
            featureExecutionStateRef.current.hasExecuted = false;
            featureExecutionStateRef.current.executionId = null;
            featureExecutionStateRef.current.featureId = null;
            
            setIsFeatureWorkflowExecuting(false); // Clear feature flag
            setIsExecutingWorkflow(false);
            setWorkflowExecutionId(null);
            
            // Send personalized completion message
            if (localChatbotRef.current?.addMessage) {
              localChatbotRef.current.addMessage({
                text: templates.completion,
                sender: "bot",
                isHtml: false,
                isFeatureEnablementComplete: true,
                featureId: featureId,
              });
            }
            
            // Forward results to component action handler
            if (handleComponentAction) {
              handleComponentAction("strategy_creation_complete", null, {
                ...workflowResults,
                triggeredManually: true,
                featureId,
                onSaveComplete: (savedPlanId) => {
                  console.log("✅ Manual feature workflow: Content saved to plan:", savedPlanId);
                },
                onSaveError: (error) => {
                  console.error("❌ Manual feature workflow: Save failed:", error);
                },
              });
            }
          },
          onError: (error) => {
            console.error("❌ Manual feature workflow failed:", error);
            
            // CRITICAL: Clear durable registry on error so user can retry
            console.log(`🧹 FEATURE ERROR: Clearing durable registry for ${featureId} to allow retry`);
            featureEnablementRegistry.clear(featureId);
            
            // Reset useRef state on error
            featureExecutionStateRef.current.hasExecuted = false;
            featureExecutionStateRef.current.executionId = null;
            featureExecutionStateRef.current.featureId = null;
            
            setIsFeatureWorkflowExecuting(false); // Clear feature flag
            setIsExecutingWorkflow(false);
            setWorkflowExecutionId(null);
            
            if (localChatbotRef.current?.addMessage) {
              localChatbotRef.current.addMessage({
                text: `❌ Failed to generate ${templates.featureName}. Please try again or use the chat to request help.`,
                sender: "bot",
                isHtml: false,
              });
            }
          },
        };

        // Start the workflow using feature_enablement workflow ID
        // CRITICAL FIX: Using 'autonomous_design_strategy' caused StrategyWorkflowManager
        // to call getWorkflowSteps('autonomous_design_strategy') which returns 4 steps,
        // creating a second progress UI. Using 'feature_enablement' keeps the workflow
        // aligned with the feature templates and prevents duplicate progress UIs.
        console.log("═══════════════════════════════════════════════════════════════");
        console.log("🎯 FEATURE ENABLEMENT TRACE [6/10]: Calling strategyWorkflowManager.startStrategyWorkflow");
        console.log("🎯 Workflow ID: feature_enablement");
        console.log("🎯 Params being passed:", JSON.stringify({
          hasDesignChallenge: !!workflowParams.designChallenge,
          triggeredManually: workflowParams.triggeredManually,
          featureId: workflowParams.featureId,
          executionPlanSteps: workflowParams.executionPlan?.steps
        }, null, 2));
        console.log("═══════════════════════════════════════════════════════════════");
        
        await strategyWorkflowManager.startStrategyWorkflow(
          'feature_enablement', // Use feature_enablement to match templates
          workflowParams,
          callbacks,
        );
        
        console.log("🎯 FEATURE ENABLEMENT TRACE [10/10]: strategyWorkflowManager.startStrategyWorkflow COMPLETED");
      } catch (error) {
        console.error("❌ triggerManualFeatureWorkflow: Failed to start workflow:", error);
        
        // CRITICAL: Clear durable registry on catch block error so user can retry
        console.log(`🧹 FEATURE CATCH: Clearing durable registry for ${featureId} to allow retry`);
        featureEnablementRegistry.clear(featureId);
        
        // Reset useRef state on catch error
        featureExecutionStateRef.current.hasExecuted = false;
        featureExecutionStateRef.current.executionId = null;
        featureExecutionStateRef.current.featureId = null;
        
        setIsFeatureWorkflowExecuting(false); // Clear feature flag
        setIsExecutingWorkflow(false);
        setWorkflowExecutionId(null);
        
        if (localChatbotRef.current?.addMessage) {
          localChatbotRef.current.addMessage({
            text: "❌ Failed to start content generation. Please try again.",
            sender: "bot",
            isHtml: false,
          });
        }
      }
    }, [
      currentDesignChallenge,
      programsOfWork,
      methods,
      tools,
      deliverables,
      handleComponentAction,
      isFeatureWorkflowExecuting,
    ]);

    // Expose chatbot methods through the ref
    React.useImperativeHandle(
      forwardedChatbotRef,
      () => ({
        startNewChat: (forceReset = false) => {
          if (localChatbotRef.current?.startNewChat) {
            return localChatbotRef.current.startNewChat(forceReset);
          }
        },
        openChat: () => {
          if (localChatbotRef.current?.openChat) {
            localChatbotRef.current.openChat();
          }
        },
        handleSend: (message, refinementContext = null) => {
          if (localChatbotRef.current?.handleSend) {
            return localChatbotRef.current.handleSend(message, refinementContext);
          }
        },
        addMessage: (message) => {
          // CRITICAL FIX: Now calls localChatbotRef directly - no recursion possible
          // The old guard was preventing ALL messages from rendering
          console.log("📨 EmbeddedChatPanel.addMessage called:", message?.text?.substring(0, 50));
          if (localChatbotRef.current?.addMessage) {
            return localChatbotRef.current.addMessage(message);
          }
        },
        toggleChat: () => {
          if (localChatbotRef.current?.toggleChat) {
            localChatbotRef.current.toggleChat();
          }
        },
        // NEW: Expose manual feature workflow trigger
        triggerManualFeatureWorkflow: (featureId) => {
          return triggerManualFeatureWorkflow(featureId);
        },
      }),
      [triggerManualFeatureWorkflow],
    );

    // Handle create_plan SSE completion.
    // Plan does not exist yet (navigated to /plan with no id) — handleStrategyCreationComplete
    // calls savePlan() to create it, then saves the PS report via /api/product-strategy/save.
    useEffect(() => {
      const handler = (e) => {
        if (e.detail?.capability !== 'create_plan') return;
        const { briefData, psReport, designChallenge: dc } = e.detail;
        if (!briefData) return;

        const completionData = {
          savedPlanId:e.detail.savedPlanId,
          receiptId:e.detail.receiptId,
          // Brief fields
          plan_name: briefData.plan_name,
          short_summary: briefData.short_summary,
          problem_statement: briefData.problem_statement,
          goal_statement: briefData.goal_statement,
          target_statement: briefData.target_statement,
          objectivesStatement: briefData.objectives_statement,
          stakeholders: briefData.stakeholders_involvement,
          designChallenge: dc,
          // PS report fields — ComponentActionHandler saves these via /api/product-strategy/save
          ...(psReport || {}),
          // No action plan programs — Quick Plan is strategy-only
          workflowMetadata: { executionPlan: { customProgramsSkipped: true } },
          onSaveComplete: (savedPlanId) => {
            console.log("✅ Quick Plan SSE: brief + PS saved for plan:", savedPlanId);
            chatStateManager.updateConversationPlanId(savedPlanId);
          },
          onSaveError: (error) => {
            console.error("❌ Quick Plan SSE save failed:", error);
          },
        };

        if (handleComponentAction) {
          handleComponentAction("strategy_creation_complete", null, completionData);
        }
      };

      window.addEventListener('pabs:generationComplete', handler);
      return () => window.removeEventListener('pabs:generationComplete', handler);
    }, [handleComponentAction]);

    useEffect(() => {
      const handler = (e) => {
        const { message } = e.detail || {};
        if (!message) return;
        if (localChatbotRef.current?.handleSend) {
          localChatbotRef.current.handleSend(message);
        }
      };
      window.addEventListener('pabs:runTask', handler);
      return () => window.removeEventListener('pabs:runTask', handler);
    }, []);

    // Reset the welcome flag whenever we switch plans
    useEffect(() => {
      if (!currentPlanId) return;
      welcomeInjectedRef.current = false;
    }, [currentPlanId]);


    return (
      <Box
        ref={outerPanelRef}
        width={isCollapsed ? "0" : "100%"}
        height="100%"
        bg="white"
        transition="width 0.3s ease"
        position="relative"
        overflow="hidden"
        isolation="isolate"
        css={{
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          // On mobile when open: fixed full-screen overlay
          ...(!isCollapsed && {
            "@media (max-width: 767px)": {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: "0",
              width: "100%",
              height: "100dvh",
              zIndex: 2000,
              backgroundColor: "white",
            },
          }),
        }}
      >
        {/* FIX #43: ALWAYS render the chat structure so FloatingChatbot is mounted before messages are sent */}
        {/* Use visibility:hidden when collapsed to keep component mounted but invisible */}
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          visibility={isCollapsed ? "hidden" : "visible"}
          position={isCollapsed ? "absolute" : "relative"}
        >
          <Flex
            justify="space-between"
            align="center"
            p={3}
            minHeight="60px"
            height="60px"
            flexShrink={0}
            //borderBottom="1px"
            // borderColor="gray.200"
            //bg="gray.50"
          >
            <HStack spacing={2} flex="1">
              <Popover
                placement="bottom-start"
                isOpen={isPopoverOpen}
                onClose={handlePopoverClose}
                closeOnBlur={true}
                returnFocusOnClose={false}
              >
                <PopoverTrigger>
                  <IconButton
                    icon={
                      <span className="material-icons-outlined">
                        history
                      </span>
                    }
                    size="sm"
                    variant="ghost"
                    aria-label="Show conversations"
                    onClick={isPopoverOpen ? handlePopoverClose : handlePopoverOpen}
                  />
                </PopoverTrigger>
                <PopoverContent width="300px">
                  <PopoverArrow />
                  <PopoverBody p={2}>
                    <Box maxHeight="280px" overflowY="auto">
                      <ConversationsList
                        currentPlanId={currentPlanId}
                        chatbotRef={localChatbotRef}
                        onClose={handlePopoverClose}
                      />
                    </Box>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
              <Tooltip label="New Chat">
                <IconButton
                  icon={
                    <span className="material-icons-outlined">
                      add_comment
                    </span>
                  }
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (
                      localChatbotRef.current &&
                      localChatbotRef.current.startNewChat
                    ) {
                      localChatbotRef.current.startNewChat(true); // Force reset when explicitly clicking New Chat
                    }
                  }}
                />
              </Tooltip>

              {(isExecutingWorkflow || workflowInitialized) && (
                <Text fontSize="xs" color="blue.500">
                  🤖 Creating strategy...
                </Text>
              )}
            </HStack>
            {/* Mobile back control; desktop panel visibility lives in the top navigation. */}
            <Tooltip label="Back to Plan">
              <IconButton
                display={{ base: "flex", md: "none" }}
                icon={
                  <span className="material-icons-outlined">arrow_back</span>
                }
                onClick={toggleCollapse}
                size="md"
                variant="ghost"
                aria-label="Back to plan"
                flexShrink={0}
              />
            </Tooltip>
          </Flex>

          <Box flex="1" overflow="hidden" minHeight="0">
            <Box
              height="100%"
              width="100%"
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <FloatingChatbot
                key={`embedded-chat-stable-${location.state?._cacheKey || currentPlanId || "default"}`}
                ref={localChatbotRef}
                currentDesignChallenge={currentDesignChallenge}
                selectedPrograms={selectedPrograms}
                shortSummary={shortSummary}
                goalStatement={goalStatement}
                problemStatement={problemStatement}
                targetStatement={targetStatement}
                objectivesStatement={objectivesStatement}
                deliverablesStatement={deliverablesStatement}
                metricsStatement={metricsStatement}
                stakeholdersCustomersInvolvement={
                  stakeholdersCustomersInvolvement
                }
                finalProgramSequence={finalProgramSequence}
                totalDuration={totalDuration}
                handleComponentAction={handleComponentAction}
                handleDurationChange={handleComponentAction}
                isEmbedded={true}
                planId={currentPlanId}
                currentPlanId={currentPlanId}
                currentWorkflowId={currentWorkflowId}
                uiConfig={uiConfig}
                programsOfWork={programsOfWork}
                methods={methods}
                tools={tools}
                deliverables={deliverables}
                isStrategyCreationMode={isStrategyCreationMode}
                strategyWorkflowManager={strategyWorkflowManager}
                planContext={planContext}
                isCollapsed={isCollapsed}
                onNavigateToView={onNavigateToView}
                currentView={currentView}
                onOpenShareModal={onOpenShareModal}
                onOpenMcpModal={onOpenMcpModal}
                onOpenLearnings={onOpenLearnings}
                onLearningsCountChanged={onLearningsCountChanged}
                onExecutionStateChange={onExecutionStateChange}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

EmbeddedChatPanel.displayName = "EmbeddedChatPanel";

export default EmbeddedChatPanel;
